import React, { useEffect, useRef, useState } from "react"
import types from "prop-types"
import clsx from "clsx"
import {
  ACTIVITY_HUB_DASHBOARD_PATH,
  ACTIVITY_HUB_SUBMISSIONS_PATH,
  ACTIVITY_HUB_TASKS_PATH,
  ACTIVITY_HUB_FORMS_PATH,
  ACTIVITY_HUB_REPORTS_PATH,
  ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH,
  ACTIVITY_HUB_SURVEYS_PATH,
  ACTIVITY_HUB_SURVEYS_SURVEY_CONFIGURATIONS_ROOT,
  ACTIVITY_HUB_SURVEYS_SURVEY_RECIPIENTS_ROOT,
  ADMIN_PATH,
} from "utils/routeHelpers"
import { useTabNavigationWithKeyboard } from "hooks"
import GlobalHeaderNavItem from "./GlobalHeaderNavItem"

const DEFAULT_TABS = [{
  title: "Home",
  icon: "home",
  path: ACTIVITY_HUB_DASHBOARD_PATH,
}, {
  title: "Submissions",
  icon: "submissions",
  path: ACTIVITY_HUB_SUBMISSIONS_PATH,
}, {
  title: "Tasks",
  icon: "tasks",
  path: ACTIVITY_HUB_TASKS_PATH,
}, {
  title: "Forms",
  icon: "forms",
  path: ACTIVITY_HUB_FORMS_PATH,
}, {
  title: "Reports",
  icon: "reports",
  options: [{
    title: "Dashboards",
    path: ACTIVITY_HUB_REPORTS_PATH,
  }, {
    title: "Self-Service Reports",
    path: ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH,
  }],
},
]

const ADDITIONAL_TABS = {
  surveys: {
    title: "Surveys",
    icon: "surveys",
    options: [{
      title: "Survey Configurations",
      path: ACTIVITY_HUB_SURVEYS_SURVEY_CONFIGURATIONS_ROOT,
    }, {
      title: "Survey Batches",
      path: ACTIVITY_HUB_SURVEYS_PATH,
    }, {
      title: "Survey Recipients",
      path: ACTIVITY_HUB_SURVEYS_SURVEY_RECIPIENTS_ROOT,
    }],
  },
  admin: {
    title: "Admin",
    icon: "admin",
    path: ADMIN_PATH,
  },
}

const GlobalHeaderTabs = ({ currentTab = "", additionalTabs = [], mobileMenuVisible = false }) => {
  const allTabs = [...DEFAULT_TABS]
  const clickedRef = useRef(false)

  const preventDoubleClick = (e) => {
    if (clickedRef.current) {
      e.preventDefault()
      return
    }

    clickedRef.current = true
  }

  if (additionalTabs.includes("surveys")) {
    allTabs.splice(4, 0, ADDITIONAL_TABS.surveys)
  }

  if (additionalTabs.includes("admin")) {
    allTabs.push(ADDITIONAL_TABS.admin)
  }

  const [navIsHorizontal, setNavIsHorizontal] = useState(window.innerWidth >= 1024)

  const { tabRef, handleKeyDown } = useTabNavigationWithKeyboard(allTabs)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setNavIsHorizontal(true)
      } else {
        setNavIsHorizontal(false)
      }
    }
    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return (
    <nav className={clsx("activity-hub-main-nav", !mobileMenuVisible && "hidden")} aria-label="main menu">
      <ul
        className="activity-hub-main-nav-list"
        role={navIsHorizontal ? "tablist" : "menu"}
        aria-orientation={navIsHorizontal ? "horizontal" : "vertical"}
        aria-label="Activity Hub"
      >
        {
          allTabs.map((tab, index) => (
            <GlobalHeaderNavItem
              key={tab.title}
              tab={tab}
              isActiveTab={currentTab === tab.icon}
              index={index}
              tabRef={tabRef}
              navIsHorizontal={navIsHorizontal}
              handleKeyDown={handleKeyDown}
              onClick={preventDoubleClick}
            />
          ))
        }
      </ul>
    </nav>
  )
}

GlobalHeaderTabs.propTypes = {
  currentTab: types.string,
  additionalTabs: types.arrayOf(types.string),
  mobileMenuVisible: types.bool,
}

export default GlobalHeaderTabs
